/**
 * @description Shuffles the array following the Fisher-Yates Shuffle algorithm.
 *
 * The Fisher–Yates shuffle is an algorithm for generating a random permutation of a
 * finite sequence—in plain terms, the algorithm shuffles the sequence.
 *
 * Reference - https://en.wikipedia.org/wiki/Fisher%E2%80%93Yates_shuffle
 * @author Leo Santos
 * @param {array}  Required
 * @returns shuffled array
 */
export const shuffle = <T>(array: T[]): T[] => {
  /**
   * Fisher–Yates shuffle
   *
   * The Fisher–Yates shuffle is an algorithm for generating a random
   * permutation of a finite sequence—in plain terms, the algorithm
   * shuffles the sequence.
   *
   * URL - https://en.wikipedia.org/wiki/Fisher%E2%80%93Yates_shuffle
   */
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }

  return array;
};

/**
 * @description Triggers a file download from a URL by creating a temporary anchor and clicking it.
 * @param url The URL of the file to download.
 * @param fileName The name to use for the downloaded file.
 */
export const downloadFileURL = (url: string, fileName: string): void => {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * @description Opens a new tab with the URL provided.
 * @author Leo Santos
 * @param {url}  Required
 * @fires window.open(url, '_blank', 'noopener,noreferrer')
 */
export const openInNewTab = (url: string) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

  if (newWindow) newWindow.opener = null;
};

/**
 * @description Formats the Google URL to open a new tab with the company page.
 * @author Leo Santos
 * @param {Object} company Object: company: { company_name, city, state }
 * @returns Base Google URL - https://www.google.com/search?q=
 */
export const formatGoogleURL = (company: {
  google?: { places_url?: string };
  company_name?: string;
  city?: string;
  state?: string;
}) => {
  if (company.google?.places_url) {
    return company.google.places_url;
  }

  let baseURL = 'https://www.google.com/search?q=';

  if (company?.company_name) {
    const name = company.company_name.replace('&', '%26');
    baseURL += name.replace(' ', '+');
    baseURL += `+${company.city}+${company.state}`;
  }

  return baseURL;
};

const dateOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

export const formatDateTime = (value: string) =>
  new Date(value).toLocaleString([], dateOptions);

export const formatString = (value: string) => value.toLowerCase();

export const formatUSD = (value: number, showCents = true) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(value);

export const calculateTotalOrderAmount = (
  totalAmount: number,
  percentOff: number,
  amountOff: number,
  extraTradeAmountOff: number
) => {
  if (amountOff !== undefined) {
    totalAmount -= amountOff;
  }

  if (extraTradeAmountOff !== undefined) {
    totalAmount -= extraTradeAmountOff;
  }

  if (percentOff !== undefined) {
    totalAmount -= (percentOff / 100) * totalAmount;
  }

  // Ensure the total amount is not negative or NaN
  const newTotalAmount = Math.max(totalAmount, 0);

  return newTotalAmount;
};
/**
 * @description Generates 8 unique characters
 * @author Leo Santos
 * @returns unique identifier with 8 characters
 */
export const uidGenerator = () => {
  const S4 = () => {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return S4() + S4();
};

/**
 * @description Generates 8 unique characters
 * @author Leo Santos
 * @returns unique identifier with 8 characters
 */
export const stateNameToAbbreviation = (state: string) => {
  const data: Record<string, string> = {
    Arizona: 'AZ',
    Alabama: 'AL',
    Alaska: 'AK',
    Arkansas: 'AR',
    California: 'CA',
    Colorado: 'CO',
    Connecticut: 'CT',
    'District of Columbia': 'DC',
    Delaware: 'DE',
    Florida: 'FL',
    Georgia: 'GA',
    Hawaii: 'HI',
    Idaho: 'ID',
    Illinois: 'IL',
    Indiana: 'IN',
    Iowa: 'IA',
    Kansas: 'KS',
    Kentucky: 'KY',
    Louisiana: 'LA',
    Maine: 'ME',
    Maryland: 'MD',
    Massachusetts: 'MA',
    Michigan: 'MI',
    Minnesota: 'MN',
    Mississippi: 'MS',
    Missouri: 'MO',
    Montana: 'MT',
    Nebraska: 'NE',
    Nevada: 'NV',
    'New Hampshire': 'NH',
    'New Jersey': 'NJ',
    'New Mexico': 'NM',
    'New York': 'NY',
    'North Carolina': 'NC',
    'North Dakota': 'ND',
    Ohio: 'OH',
    Oklahoma: 'OK',
    Oregon: 'OR',
    Pennsylvania: 'PA',
    'Rhode Island': 'RI',
    'South Carolina': 'SC',
    'South Dakota': 'SD',
    Tennessee: 'TN',
    Texas: 'TX',
    Utah: 'UT',
    Vermont: 'VT',
    Virginia: 'VA',
    Washington: 'WA',
    'West Virginia': 'WV',
    Wisconsin: 'WI',
    Wyoming: 'WY',
    'American Samoa': 'AS',
    Guam: 'GU',
    'Northern Mariana Islands': 'MP',
    'Puerto Rico': 'PR',
    'U.S. Virgin Islands': 'VI',
    'U.S. Minor Outlying Islands': 'UM',
  };

  return data[state] ?? state;
};

export const isFutureDate = (dateString: Date | string) => {
  const currentDate = new Date();
  const inputDate = new Date(dateString);

  return inputDate > currentDate;
};

const DATE_ONLY_ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Returns true if the given date is strictly before today (at day granularity).
 * Returns false if date is missing or invalid.
 * Date-only ISO strings (YYYY-MM-DD) are parsed as local midnight to avoid UTC drift.
 */
export const isExpired = (date?: string): boolean => {
  if (!date) {
    return false;
  }

  let input: Date;

  if (DATE_ONLY_ISO.test(date)) {
    const [y, m, d] = date.split('-').map(Number);
    input = new Date(y, m - 1, d);
  } else {
    input = new Date(date);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  input.setHours(0, 0, 0, 0);

  return input < today;
};

export const capitalizeFirstLetter = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
